import React, { useRef, useState, useEffect } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import styles from "../../_css/ReportingAndSterring.module.css";
import { useClickOutsideWithCallback } from "../hooks/useClickOutside.js";
import { getAllTicketsForReportingSteering } from "../../requests.js";
import ResponseLoader from "../common/ResponseLoader.js";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer.js";
import { useDashboardFilters } from "./context/DashboardContext.js";

const filterTabs = [
    { id: 'during-feedback', label: 'During Feedback', value: 'during_feedback' },
    { id: 'during-role-play', label: 'During Roleplay', value: 'during_role_play' },
    { id: 'human-oversight', label: 'Human Oversight', value: 'human_oversight' }
];

const statusOptions = ['All', 'Open', 'In Progress', 'Resolved'];

const ITEMS_PER_PAGE = 4;

const FeedbackOverview = () => {
    const [activeFilter, setActiveFilter] = useState('During Feedback');
    const [showDropdown, setShowDropdown] = useState(false);
    const [statusFilter, setStatusFilter] = useState('All');
    const [ticketData, setTicketData] = useState([]);
    const [metricCards, setMetricCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const dropdownRef = useRef(null);
    const dispatch = useDispatch();
    const { filters } = useDashboardFilters();

    useClickOutsideWithCallback(dropdownRef, () => {
        setShowDropdown(false);
    });

    const toggleDropdown = () => setShowDropdown(!showDropdown);

    const selectOption = (option) => {
        setStatusFilter(option);
        setShowDropdown(false);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    // Fetch tickets data with dashboard filters
    const fetchTickets = async (filterType = activeFilter) => {
        try {
            setLoading(true);
            dispatch(startLoader());
            // Prepare query parameters with dashboard filters
            const filterValue = filterTabs.find(tab => tab.label === filterType)?.label;
            const startDate = filters.dateRange?.from;
            const endDate = filters.dateRange?.to;
            const companyName = filters.company;

            const response = await getAllTicketsForReportingSteering({ startDate, endDate, companyName, filterValue });

            if (response && response.success) {
                setTicketData(response.ticketData || []);
                setMetricCards(response.metricCards || []);
            } else {
                throw new Error(response?.message || 'Failed to fetch tickets');
            }

            setError(null);
        } catch (err) {
            console.error('Error fetching tickets:', err);
            setError('Failed to load tickets data');
            setTicketData([]);
            setMetricCards([]);
        } finally {
            setLoading(false);
            dispatch(stopLoader());
        }
    };

    // Map status to color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'open':
                return '#DC2626';
            case 'in progress':
                return '#D97706';
            case 'resolved':
                return '#16A34A';
            default:
                return '#6B7280';
        }
    };

    // Filter tickets based on status
    const filteredTickets = ticketData.filter(ticket => {
        if (statusFilter === 'All') return true;
        return ticket.status?.toLowerCase() === statusFilter.toLowerCase();
    });

    // Pagination calculations
    const totalPages = Math.ceil(filteredTickets.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedTickets = filteredTickets.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Handle filter tab change
    const handleFilterChange = (filterLabel) => {
        setActiveFilter(filterLabel);
        setCurrentPage(1);
        fetchTickets(filterLabel);
    };

    // Fetch data when dashboard filters change
    useEffect(() => {
        setCurrentPage(1);
        fetchTickets();
    }, [filters]);

    // Initial data fetch
    useEffect(() => {
        fetchTickets();
    }, []);

    // Render loading state within the container
    const renderContent = () => {
        if (loading) {
            return (
                <div className={styles.loadingContainer}>
                    <ResponseLoader />
                </div>
            );
        }

        if (error) {
            return (
                <div className={styles.errorMessage}>
                    No Data To Preview
                </div>
            );
        }

        return (
            <>
                {/* Metric Cards */}
                <div className={styles.metricCardsGrid}>
                    {metricCards.map((card, index) => (
                        <div
                            key={index}
                            className={styles.metricCard}
                            style={{
                                border: `1px solid ${card.borderColor}`,
                                background: card.bgColor
                            }}
                        >
                            <div className={styles.metricLabel}>
                                {card.label}
                            </div>
                            <div className={styles.metricValue}>
                                {card.value}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Table */}
                <div className={styles.feedbackTableContainer}>
                    <table className={styles.feedbackTable}>
                        <colgroup>
                            <col style={{ width: '120px' }} />
                            <col style={{ width: 'auto' }} />
                            <col style={{ width: '150px' }} />
                            <col style={{ width: '200px' }} />
                            <col style={{ width: '150px' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                {['Ticket ID', 'Category', 'Status', 'Audience', 'Created Date'].map((heading) => (
                                    <th
                                        key={heading}
                                        className={styles.feedbackTableHeader}
                                    >
                                        {heading}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedTickets.length > 0 ? (
                                paginatedTickets.map((ticket) => (
                                    <tr
                                        key={ticket.id}
                                        className={styles.feedbackTableRow}
                                    >
                                        <td className={styles.tableCell}>{ticket.id}</td>
                                        <td className={styles.tableCell}>{ticket.category}</td>
                                        <td className={styles.tableCell}>
                                            <div className={styles.statusIndicator}>
                                                <div
                                                    className={styles.statusDot}
                                                    style={{ backgroundColor: ticket.statusColor || getStatusColor(ticket.status) }}
                                                ></div>
                                                <span className={styles.statusText}>{ticket.status}</span>
                                            </div>
                                        </td>
                                        <td className={styles.tableCell}>{ticket.audience}</td>
                                        <td className={styles.tableCell}>{ticket.createdDate}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className={styles.noDataCell}>
                                        No tickets found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Custom Pagination */}
                    <div style={{ padding: '0 ' }}>
                        {totalPages > 1 && (
                            <div className={styles.paginationWrapper}>
                                {/* Previous Button */}
                                <button
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    className={styles.paginationNavButton}
                                    style={{
                                        color: currentPage === 1 ? '#9CA3AF' : '#23B4C3',
                                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    <ChevronLeft size={16} />
                                    Previous
                                </button>

                                {/* Page Numbers */}
                                <div className={styles.pageNumbersContainer}>
                                    {[...Array(totalPages)].map((_, idx) => {
                                        const pageNum = idx + 1;
                                        const isActive = pageNum === currentPage;
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={styles.pageNumberButton}
                                                style={{
                                                    border: isActive ? '1px solid #23B4C3' : "1px solid #E5E7EB",
                                                    backgroundColor: isActive ? '#E0F7FA' : '#FFFFFF',
                                                    color: isActive ? '#23B4C3' : '#6B7280',
                                                    fontWeight: isActive ? 500 : 400,
                                                }}
                                            >
                                                {pageNum.toString().padStart(2, '0')}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Next Button */}
                                <button
                                    disabled={currentPage === totalPages}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    className={styles.paginationNavButton}
                                    style={{
                                        color: currentPage === totalPages ? '#9CA3AF' : '#23B4C3',
                                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    Next
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className={styles.feedbackContainer}>
            <div className={styles.feedbackHeader}>
                {/* Filter Tabs */}
                <div className={styles.filterTabs}>
                    {filterTabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => handleFilterChange(tab.label)}
                            className={`${styles.filterTab} ${activeFilter === tab.label ? styles.filterTabActive : ''}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Status Filter Dropdown */}
                <div className={styles.dropdownWrapper}>
                    <button
                        onClick={toggleDropdown}
                        className={styles.dropdownButton}
                    >
                        {statusFilter} <ChevronDown size={16} color="#6b7280" />
                    </button>

                    {showDropdown && (
                        <div className={styles.dropdownMenu} ref={dropdownRef}>
                            {statusOptions.map((option, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => selectOption(option)}
                                    className={`${styles.dropdownOption} ${statusFilter === option ? styles.dropdownOptionActive : ''}`}
                                >
                                    {option}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Render dynamic content (loading, error, or data) */}
            {renderContent()}
        </div>
    );
};

export default FeedbackOverview;